import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Provider } from 'react-redux';
import { isScreenWidthMobile } from '../app/components/custom-hooks/screen-detector';
import { postData } from '../common/api';
import { createEmbeddedPageRequest } from '../common/apiRequest';
import { changeCart, refreshAJAXCart } from '../common/cart';
import { getOfferEndpoint } from '../common/constants';
import { createElementAfter, createElementBefore } from '../common/html';
import { getShop } from '../common/pageData';
import { GlobalStyle } from '../app/components/skins';
// eslint-disable-next-line import/no-cycle
import Init from '../app/containers/Init';
// import { logger } from '../common/logger';
import { setOfferViewed, setSubscriptionViewed } from '../common/offer';
import { getMerchantSpecificThemeFlags } from '../app/components/helpers/offer';
import { ADD_TO_CART_EVENTAPI } from '../app/containers/Init/constants';
import SubscriptionUpsellPage from '../app/containers/SubscriptionUpsellPage';
import { goToCart } from '../common/navigation';
import { logger } from '../common/logger';
import { logRuntimeError, logtailMiddleware } from '../common/utils';

export const checkSubscriptionUpsellAndDisplay = async ({
  cartPageDiscountConfig, // optional
  isDiscountCodeEnabled,
  apiCart,
  widgets,
}) => {
  let displayAfterMs = 0;
  if (
    ['3f427d-3.myshopify.com', 'monk-demo.myshopify.com'].includes(getShop())
  ) {
    displayAfterMs = 1000;
  }

  let widget = null;
  for (let i = 0; i < widgets.length; i += 1) {
    if (widgets[i].offerType === 'subscription_upsell') {
      widget = widgets[i];
      break;
    }
  }

  if (!widget) {
    return;
  }

  const { mrWidgetID, sections } = widget;

  try {
    const source = 'cart_page';
    const req = await createEmbeddedPageRequest({
      source,
      offerType: 'subscription_upsell',
      apiCart,
      widget,
    });
    const { cart: mrCart } = req;

    const response = await postData(getOfferEndpoint(), req);

    if (!response) {
      return;
    }

    const { offer } = response;
    logtailMiddleware({
      widgetId: widget.id,
      basicOfferData: {
        subscription: offer.subscription,
      },
      offerName: 'subscriptionUpsell',
    });
    setTimeout(() => {
      displaySubscriptionUpsell({
        offer,
        mrCart,
        cartPageDiscountConfig,
        isDiscountCodeEnabled,
        mrWidgetID,
        sections,
      });
    }, displayAfterMs);
  } catch (e) {
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'subscriptionUpsellModule',
        level: 'error',
        functionName: 'checkSubscriptionUpsellAndDisplay',
      },
      errorStack: e,
    });
    logger.log('error', e);
  }
};

const displaySubscriptionUpsell = async ({
  offer,
  mrCart,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  sections,
  mrWidgetID,
}) => {
  try {
    const containers = [];
    for (let i = 0; i < sections.length; i += 1) {
      const section = sections[i];
      const { selector, position } = section;
      const { sectionType } = section;

      containers.push({
        selector,
        position,
        containerID: getSubscriptionUpsellContainerID({
          sectionType,
          selector,
          mrWidgetID,
        }),
        isDrawer: sectionType === 'cart_drawer',
      });
    }

    const tasks = [];
    for (let i = 0; i < containers.length; i += 1) {
      const { containerID, selector, position, isDrawer } = containers[i];
      tasks.push(
        createContainerAndRender({
          offer,
          mrCart,
          containerID,
          selector,
          position,
          isDrawer,
        }),
      );
    }

    const result = await Promise.all(tasks);
    const rendered = result.reduce((acc, curr) => acc || curr.value, false);
    const {
      mr_offer_id,
      subscription: { cartItemsSubscriptionData },
    } = offer;

    if (rendered) {
      setOfferViewed({
        mr_offer_id,
        checkDiscount: false,
        cartPageDiscountConfig,
        isDiscountCodeEnabled,
      });
      setSubscriptionViewed({
        mr_offer_id,
        cartItemsSubscriptionData,
        mrCart,
      });
    }
  } catch (e) {
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'subscriptionUpsellModule',
        level: 'error',
        functionName: 'displaySubscriptionUpsell',
      },
      errorStack: e,
    });
  }
};

const getSubscriptionUpsellContainerID = ({ sectionType, mrWidgetID }) => {
  // as the ids were predefined and old stores have this id, we cannot change it
  const trigger = 'cp';
  const offer = 'subscription_upsell';
  let section = sectionType;

  switch (sectionType) {
    case 'cart_page':
      section = 'page';
      break;

    case 'cart_drawer':
      section = 'drawer';
      break;

    default:
      break;
  }

  let id = `mr-div-embedded-${trigger}-${offer}-${section}`;
  if (mrWidgetID) {
    id = `${id}-${mrWidgetID}`;
  }
  return id;
};

const createContainerAndRender = ({
  offer,
  mrCart,
  containerID,
  selector,
  position,
  isDrawer,
}) => {
  const { mr_offer_id, subscription } = offer;
  const { cartItemsSubscriptionData } = subscription;
  const selectedNodes = [];

  if (!selector) return false;
  const nodes = document.querySelectorAll(selector);

  for (let j = 0; j < nodes.length; j += 1) {
    if (nodes[j] && nodes[j].offsetHeight > 0) {
      selectedNodes.push(nodes[j]);
    }
  }

  if (selectedNodes.length < 1) {
    return false;
  }

  let rendered = false;
  for (let i = 0; i < selectedNodes.length; i += 1) {
    let sellingPlanGroups = null;
    if (cartItemsSubscriptionData && cartItemsSubscriptionData.length > i) {
      sellingPlanGroups = cartItemsSubscriptionData[i].sellingPlanGroups;
    }
    const isClose =
      sellingPlanGroups === null || sellingPlanGroups.length === 0;

    if (!isClose) {
      rendered = true;
    }

    // eslint-disable-next-line no-param-reassign
    containerID = `${containerID}-${i}`;
    const existing = document.getElementById(containerID);

    // create the container if it doesn't exist
    if (!existing) {
      const elem = document.createElement('div');
      elem.setAttribute('id', containerID);
      if (position === 'before') {
        createElementBefore(selectedNodes[i], elem);
      } else {
        createElementAfter(selectedNodes[i], elem);
      }
    }

    const keys = {};

    // eslint-disable-next-line no-loop-func
    const handleSubscription = async ({
      sellingPlanId,
      key,
      spVariantId,
      callAddEventApi,
      isRefresh,
      cartIndex,
    }) => {
      const { quantity, sp_product_id } = mrCart.find(
        (_, itemIndex) => itemIndex === cartIndex,
      );

      let latestKey = key;
      if (keys[cartIndex]) {
        latestKey = keys[cartIndex];
      }

      if (callAddEventApi) {
        await subscriptionAddEvent({
          sp_product_id,
          sp_variant_id: spVariantId,
          quantity,
          mr_offer_id,
        });
      }

      const { items } = await changeCart(latestKey, quantity, sellingPlanId);
      refreshAJAXCart();

      const { key: updatedKey } = items.find(
        (_, itemIndex) => itemIndex === cartIndex,
      );
      keys[cartIndex] = updatedKey;

      if (isRefresh) {
        goToCart({
          isPopup: false,
          isRefreshPageAfterProductAddition:
            offer.theme.isRefreshPageAfterProductAddition,
        });
      }

      if (updatedKey.length > 0) {
        return true;
      }
    };

    renderSubscriptionUpsellInDiv({
      isClose,
      offer,
      mrCart,
      containerID,
      cartIndex: i,
      handleSubscription,
      isDrawer,
    });
  }
  return rendered;
};

export function renderSubscriptionUpsellInDiv({
  isClose,
  offer,
  mrCart,
  containerID,
  cartIndex,
  handleSubscription,
  isDrawer,
}) {
  const container = document.getElementById(containerID);
  if (!container) {
    return;
  }

  if (!isClose) {
    offer.theme = getMerchantSpecificThemeFlags({
      theme: offer.theme,
      shopURL: getShop(),
      offerType: 'subscription_upsell',
    });
  }

  const isMobile = isScreenWidthMobile();

  ReactDOM.render(
    <Provider store={store}>
      <div>
        <GlobalStyle />
        <Init />
        {!isClose && (
          <SubscriptionUpsellPage
            mrCart={mrCart}
            offer={offer}
            cartIndex={cartIndex}
            handleSubscription={handleSubscription}
            isMobile={isMobile}
            isDrawer={isDrawer}
          />
        )}
      </div>
    </Provider>,
    container,
  );
}

export const subscriptionAddEvent = async ({
  sp_product_id,
  sp_variant_id,
  quantity,
  mr_offer_id,
}) => {
  if (mr_offer_id === '') {
    return;
  }

  try {
    const req = {
      mr_offer_id,
      sp_variant_id,
      quantity,
      sp_product_id,
    };

    const apiURL = ADD_TO_CART_EVENTAPI;
    const httpArgs = [apiURL, req];
    await axios.post(...httpArgs);
    return;
  } catch (e) {
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'subscriptionUpsellModule',
        level: 'error',
        functionName: 'subscriptionAddEvent',
        extraErrorData: {
          sp_product_id,
          sp_variant_id,
          quantity,
          mr_offer_id,
        },
      },
      errorStack: e,
    });
    logger.log(e);
  }
};

let store = null;
export const setStore = st => {
  store = st;
};
